import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  writeBatch,
  doc,
  query,
  limit,
  where,
  orderBy,
  startAfter,
  serverTimestamp,
  getCountFromServer,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';

const COLLECTION = 'diagnosisCodes';
const SUB_CATEGORIES_COLLECTION = 'diagnosisSubCategories';
const META_COLLECTION = 'diagnosisCodesMeta';
const META_DOC_ID = 'catalog';
const STORAGE_BUNDLE_DIR = 'diagnosis-codes';
const BATCH_SIZE = 500;

/** How many Firestore batch.commit() calls to run in parallel (each batch ≤500 writes). */
const PARALLEL_BATCH_COMMITS = 6;

/**
 * Firestore document max is 1 MiB; stay under with headroom for protobuf vs JSON estimate.
 * @see https://firebase.google.com/docs/firestore/quotas
 */
const MAX_ESTIMATED_DOC_BYTES = 950_000;

const LOG_PREFIX = '[diagnosisCodes]';

/** Rough UTF-8 byte size of JSON (close enough to catch 1MB limit failures before commit). */
function utf8JsonBytes(obj) {
  return new TextEncoder().encode(JSON.stringify(obj)).length;
}

/**
 * Payload shape as written (timestamp replaced for sizing).
 */
function diagnosisDocPayloadForSize(row, docId, longDesc, extra = {}) {
  return {
    orderNum: row.orderNum,
    icdCode: docId,
    header: row.header,
    assignable: row.assignable === true,
    category: row.category ?? null,
    shortDesc: row.shortDesc ?? '',
    longDesc: longDesc ?? '',
    addedAt: '__SERVER_TIMESTAMP__',
    ...extra,
  };
}

function estimatedDocBytes(row, docId, longDesc, extra = {}) {
  return utf8JsonBytes(diagnosisDocPayloadForSize(row, docId, longDesc, extra));
}

/**
 * Cheap UTF-8 byte count of main text fields + margin for JSON keys / Firestore encoding.
 * Avoids full JSON.stringify for ~100k typical ICD rows.
 */
function quickUnderFirestoreLimit(row, docId, longDesc) {
  const blob = [
    typeof longDesc === 'string' ? longDesc : '',
    typeof row.shortDesc === 'string' ? row.shortDesc : '',
    row.category != null ? String(row.category) : '',
    typeof row.orderNum === 'string' ? row.orderNum : '',
    docId,
  ].join('\0');
  const textBytes = new TextEncoder().encode(blob).length;
  const jsonOverhead = 800;
  return textBytes + jsonOverhead < MAX_ESTIMATED_DOC_BYTES;
}

/**
 * Ensures a row fits Firestore's per-document size limit by truncating `longDesc` if needed.
 * @returns {{ row: Record<string, *>, truncated: boolean }}
 */
export function fitDiagnosisRowToFirestoreLimit(row) {
  const docId = normalizeIcdDocId(row.icdCode);
  const fullLong = typeof row.longDesc === 'string' ? row.longDesc : '';
  let longDesc = fullLong;
  let truncated = false;

  if (quickUnderFirestoreLimit(row, docId, longDesc)) {
    return { row: { ...row, longDesc }, truncated: false };
  }

  if (estimatedDocBytes(row, docId, longDesc) <= MAX_ESTIMATED_DOC_BYTES) {
    return { row: { ...row, longDesc }, truncated: false };
  }

  let low = 0;
  let high = fullLong.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    const slice = fullLong.slice(0, mid);
    const bytes = estimatedDocBytes(row, docId, slice, { longDescTruncated: true });
    if (bytes <= MAX_ESTIMATED_DOC_BYTES) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }
  longDesc = fullLong.slice(0, low);
  truncated = longDesc.length < fullLong.length;

  const extra = truncated ? { longDescTruncated: true } : {};
  if (estimatedDocBytes(row, docId, longDesc, extra) > MAX_ESTIMATED_DOC_BYTES) {
    console.error(`${LOG_PREFIX} Row still exceeds size limit after truncating longDesc`, {
      icdCode: docId,
      orderNum: row.orderNum,
      estimatedBytes: estimatedDocBytes(row, docId, longDesc, extra),
    });
    throw new Error(
      `Diagnosis code ${docId} (order ${row.orderNum}) exceeds Firestore size limit even after truncation.`
    );
  }

  return {
    row: {
      ...row,
      longDesc,
      ...(truncated ? { longDescTruncated: true } : {}),
    },
    truncated,
  };
}

/** Normalize ICD-10-CM code for use as Firestore document ID (strip dots, trim, uppercase). */
export function normalizeIcdDocId(icdCode) {
  return (icdCode || '').replace(/\./g, '').trim().toUpperCase();
}

/**
 * Deduplicate parsed rows by normalized ICD code; later lines win (same as single-doc overwrite).
 * Sets `icdCode` on each row to the normalized value (matches document ID).
 */
export function dedupeRowsByIcdDocId(validRows) {
  const map = new Map();
  (validRows || []).forEach((row) => {
    const id = normalizeIcdDocId(row.icdCode);
    if (!id) return;
    map.set(id, {
      ...row,
      icdCode: id,
    });
  });
  return Array.from(map.values());
}

/**
 * After dedupe: set `assignable` from header (`'1'` = true) and `category` for assignable rows
 * from the 3-character prefix code's `shortDesc` when that doc has `header === '0'`.
 * Otherwise `category` is null (missing parent, parent not header 0, or empty shortDesc).
 *
 * @param {Array<{ icdCode: string, header: string, shortDesc?: string, longDesc?: string, orderNum?: string }>} rows
 * @returns {Array<Record<string, *>>}
 */
export function enrichDiagnosisRowsWithAssignableAndCategory(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const byIcd = new Map();
  list.forEach((r) => {
    const id = normalizeIcdDocId(r.icdCode);
    if (id) byIcd.set(id, r);
  });
  return list.map((r) => {
    const icdCode = normalizeIcdDocId(r.icdCode);
    const assignable = r.header === '1';
    let category = null;
    if (assignable && icdCode.length >= 3) {
      const prefix = icdCode.slice(0, 3);
      const parent = byIcd.get(prefix);
      const desc = parent && typeof parent.shortDesc === 'string' ? parent.shortDesc.trim() : '';
      if (parent && parent.header === '0' && desc) {
        category = desc;
      }
    }
    return {
      ...r,
      icdCode,
      assignable,
      category,
    };
  });
}

export async function getDiagnosisCodes() {
  const snap = await getDocs(collection(db, COLLECTION));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Read all diagnosis sub-categories from the dedicated side collection.
 * Returns [{ prefix, label, orderNum }] sorted by label.
 */
export async function getDiagnosisSubCategories() {
  const snap = await getDocs(collection(db, SUB_CATEGORIES_COLLECTION));
  const list = snap.docs.map((d) => ({ prefix: d.id, ...d.data() }));
  list.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  return list;
}

/**
 * Read the search bundle meta doc. Returns { version, downloadUrl, storagePath, count, uploadedAt, ... } or null.
 */
export async function getDiagnosisCodesMeta() {
  const snap = await getDoc(doc(db, META_COLLECTION, META_DOC_ID));
  return snap.exists() ? snap.data() : null;
}

/**
 * Single-read aggregate count for diagnosisCodes. Used as a fallback for old
 * meta docs that pre-date the `count` field (additive fallback for the
 * Diagnosis Codes summary view -- avoids reading all ~96k documents).
 */
export async function getDiagnosisCodesCount() {
  const snap = await getCountFromServer(collection(db, COLLECTION));
  return snap.data().count || 0;
}

/**
 * Firestore fallback: search assignable codes by ICD prefix (code-like term, 3+ alphanumeric).
 * Used when the Storage search bundle is unavailable.
 */
export async function searchAssignableByCodePrefix(term, maxResults = 20) {
  const normalized = normalizeIcdDocId(term);
  if (!normalized || normalized.length < 3) return [];
  const colRef = collection(db, COLLECTION);
  const q = query(
    colRef,
    where('assignable', '==', true),
    where('icdCode', '>=', normalized),
    where('icdCode', '<=', normalized + '\uf8ff'),
    limit(maxResults),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Parse one line of CMS ICD-10-CM "tabular order" style fixed-width .txt (UTF-8).
 * @returns {{ skip?: true, error?: string, data?: { orderNum, icdCode, header, shortDesc, longDesc } }}
 */
export function parseIcd10CmTabularLine(line) {
  const raw = typeof line === 'string' ? line.replace(/\r$/, '') : '';
  if (!raw.trim()) return { skip: true };
  if (raw.length < 15) {
    return { error: 'Line too short (need at least 15 characters)' };
  }

  const orderNum = raw.slice(0, 5).trim();
  const icdCode = raw.slice(6, 13).trim();
  const header = raw.slice(14, 15).trim();
  let shortDesc = '';
  if (raw.length > 16) {
    shortDesc = raw.length >= 76 ? raw.slice(16, 76).trim() : raw.slice(16).trim();
  }
  const longDesc = raw.length > 77 ? raw.slice(77).trim() : '';

  if (!/^[A-Za-z0-9]{1,5}$/.test(orderNum)) {
    return { error: `Invalid OrderNum: "${orderNum}"` };
  }
  // ICD column (no dots in file): 3–7 alphanumeric characters (A–Z, a–z, 0–9).
  const icdTrim = icdCode.trim();
  if (!/^[A-Za-z0-9]{3,7}$/.test(icdTrim)) {
    return { error: `Invalid ICD code (need 3–7 alphanumeric): "${icdCode}"` };
  }
  if (header !== '0' && header !== '1') {
    return { error: `Invalid Header (must be 0 or 1): "${header}"` };
  }
  if (shortDesc.length > 60) {
    return { error: 'ShortDesc exceeds 60 characters after trim' };
  }

  return {
    data: {
      orderNum,
      icdCode: normalizeIcdDocId(icdTrim),
      header,
      shortDesc,
      longDesc,
    },
  };
}

/**
 * Parse full file text into valid rows and skipped line reports.
 */
export function parseIcd10CmTabularFile(text) {
  const lines = text.split(/\r?\n/);
  const valid = [];
  const skipped = [];
  lines.forEach((line, idx) => {
    const lineNum = idx + 1;
    const result = parseIcd10CmTabularLine(line);
    if (result.skip) return;
    if (result.error) {
      skipped.push({ lineNum, reason: result.error });
      return;
    }
    valid.push(result.data);
  });
  return { valid, skipped };
}

/**
 * Delete all documents in collection (chunks of BATCH_SIZE).
 * Must stay sequential: each query reads the next page after the previous deletes.
 */
async function deleteAllInChunks() {
  const colRef = collection(db, COLLECTION);
  let more = true;
  let deleteRound = 0;
  let deletedTotal = 0;
  while (more) {
    const snap = await getDocs(query(colRef, limit(BATCH_SIZE)));
    if (snap.empty) {
      more = false;
      break;
    }
    deleteRound += 1;
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    deletedTotal += snap.size;
    console.info(`${LOG_PREFIX} Delete round ${deleteRound}: removed ${snap.size} doc(s) (total deleted this import: ${deletedTotal})`);
    if (snap.size < BATCH_SIZE) more = false;
  }
  if (deleteRound === 0) {
    console.info(`${LOG_PREFIX} No existing documents to delete.`);
  }
}

/**
 * Write rows in chunks; each doc gets server `addedAt`.
 * Commits several batches in parallel to overlap network latency (bounded by PARALLEL_BATCH_COMMITS).
 */
async function writeAllInChunks(rows) {
  const total = rows.length;
  const batchCount = Math.ceil(total / BATCH_SIZE) || 0;
  console.info(
    `${LOG_PREFIX} Writing ${total} document(s) in ${batchCount} batch(es); up to ${PARALLEL_BATCH_COMMITS} commits in parallel.`
  );

  const commitBatch = async (batchIndexZeroBased) => {
    const i = batchIndexZeroBased * BATCH_SIZE;
    const chunk = rows.slice(i, i + BATCH_SIZE);
    const batchIndex = batchIndexZeroBased + 1;
    const batch = writeBatch(db);
    chunk.forEach((row) => {
      const docId = normalizeIcdDocId(row.icdCode);
      const ref = doc(db, COLLECTION, docId);
      batch.set(ref, {
        orderNum: row.orderNum,
        icdCode: docId,
        header: row.header,
        assignable: row.assignable === true,
        category: row.category ?? null,
        shortDesc: row.shortDesc,
        longDesc: row.longDesc,
        ...(row.longDescTruncated === true ? { longDescTruncated: true } : {}),
        addedAt: serverTimestamp(),
      });
    });
    try {
      await batch.commit();
    } catch (err) {
      console.error(`${LOG_PREFIX} Batch ${batchIndex}/${batchCount} FAILED at doc index ${i}`, {
        message: err?.message,
        code: err?.code,
        firstInChunk: {
          orderNum: chunk[0]?.orderNum,
          icdCode: chunk[0]?.icdCode,
        },
        err,
      });
      throw err;
    }
  };

  for (let w = 0; w < batchCount; w += PARALLEL_BATCH_COMMITS) {
    const wave = [];
    for (let j = 0; j < PARALLEL_BATCH_COMMITS && w + j < batchCount; j += 1) {
      wave.push(commitBatch(w + j));
    }
    await Promise.all(wave);
    const doneThrough = Math.min(w + PARALLEL_BATCH_COMMITS, batchCount);
    console.info(
      `${LOG_PREFIX} Committed through batch ${doneThrough}/${batchCount} (~${Math.min(doneThrough * BATCH_SIZE, total)} / ${total} docs).`
    );
  }
  console.info(`${LOG_PREFIX} All ${batchCount} write batch(es) finished.`);
}

/**
 * Build a map of distinct 3-char ICD prefix -> { prefix, label, orderNum } for
 * use as diagnosis sub-categories. Header-0 (category) rows win the label when
 * present; a 3-char header-1 (billable leaf) row only fills a prefix that has no
 * header-0 row, so codes like I10 ("Essential (primary) hypertension"), which
 * have no header-0 entry, still appear.
 * @param {Array<{ icdCode?: string, header?: string, shortDesc?: string, orderNum?: string }>} rows
 * @returns {Map<string, { prefix: string, label: string, orderNum: string }>}
 */
export function extractSubCategoryMap(rows) {
  const map = new Map();
  (rows || []).forEach((r) => {
    if (!r || !r.shortDesc) return;
    const prefix = normalizeIcdDocId(r.icdCode);
    if (prefix.length !== 3) return;
    const isHeader0 = r.header === '0';
    const existing = map.get(prefix);
    if (existing && (existing.fromHeader0 || !isHeader0)) return;
    map.set(prefix, {
      prefix,
      label: String(r.shortDesc).trim(),
      orderNum: r.orderNum || '',
      fromHeader0: isHeader0,
    });
  });
  map.forEach((v) => { delete v.fromHeader0; });
  return map;
}

/**
 * Extract one entry per distinct 3-char ICD prefix as a sub-category and write
 * to diagnosisSubCategories. Header-0 (category) rows provide the label when
 * present; otherwise a 3-char header-1 (billable leaf) row's shortDesc is used
 * so codes like I10 ("Essential (primary) hypertension"), which have no header-0
 * row, still appear. Deletes existing sub-category docs first, then writes the
 * fresh set.
 */
async function writeSubCategories(enrichedRows) {
  const colRef = collection(db, SUB_CATEGORIES_COLLECTION);

  let more = true;
  while (more) {
    const snap = await getDocs(query(colRef, limit(BATCH_SIZE)));
    if (snap.empty) { more = false; break; }
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    if (snap.size < BATCH_SIZE) more = false;
  }

  const subCats = Array.from(extractSubCategoryMap(enrichedRows).values());

  if (subCats.length === 0) return 0;

  const batchCount = Math.ceil(subCats.length / BATCH_SIZE);
  for (let i = 0; i < batchCount; i += 1) {
    const chunk = subCats.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE);
    const batch = writeBatch(db);
    chunk.forEach((sc) => {
      const ref = doc(db, SUB_CATEGORIES_COLLECTION, sc.prefix);
      batch.set(ref, {
        prefix: sc.prefix,
        label: sc.label,
        orderNum: sc.orderNum,
        updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }
  console.info(`${LOG_PREFIX} Wrote ${subCats.length} sub-category doc(s) to ${SUB_CATEGORIES_COLLECTION}.`);
  return subCats.length;
}

/**
 * Build and upload the compact search index JSON for assignable codes.
 * Writes meta doc with version and bundle location.
 * @returns {{ version: number, assignableCount: number, storagePath: string }}
 */
async function buildAndUploadSearchBundle(enrichedRows) {
  const assignable = enrichedRows
    .filter((r) => r.assignable === true)
    .map((r) => ({
      c: normalizeIcdDocId(r.icdCode),
      s: (r.shortDesc || '').trim(),
      g: r.category || null,
    }));

  const metaRef = doc(db, META_COLLECTION, META_DOC_ID);
  let prevVersion = 0;
  try {
    const metaSnap = await getDoc(metaRef);
    if (metaSnap.exists()) prevVersion = metaSnap.data().version || 0;
  } catch { /* first time */ }

  const version = prevVersion + 1;
  const storagePath = `${STORAGE_BUNDLE_DIR}/search-v${version}.json`;
  const jsonStr = JSON.stringify(assignable);
  const blob = new Blob([jsonStr], { type: 'application/json' });

  if (!storage) {
    console.warn(`${LOG_PREFIX} Firebase Storage not configured; skipping search bundle upload.`);
    return { version, assignableCount: assignable.length, storagePath: null };
  }

  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, blob, { contentType: 'application/json' });
  const downloadUrl = await getDownloadURL(storageRef);

  await setDoc(
    metaRef,
    {
      version,
      addedAt: serverTimestamp(),
      assignableCount: assignable.length,
      storagePath,
      downloadUrl,
      bundleBytes: jsonStr.length,
    },
    { merge: true }
  );

  console.info(`${LOG_PREFIX} Search bundle v${version}: ${assignable.length} assignable code(s), ${jsonStr.length} bytes → ${storagePath}`);
  return { version, assignableCount: assignable.length, storagePath };
}

/**
 * Generate (or regenerate) the search bundle from existing diagnosisCodes in Firestore.
 * Use when the bundle is missing or stale without requiring a full CMS re-upload.
 */
export async function generateSearchBundleFromExisting() {
  const snap = await getDocs(collection(db, COLLECTION));
  if (snap.empty) {
    throw new Error('No diagnosis codes in database. Upload an ICD-10-CM file first.');
  }
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const enriched = enrichDiagnosisRowsWithAssignableAndCategory(rows);
  return buildAndUploadSearchBundle(enriched);
}

/**
 * Replace entire collection with parsed rows (must be non-empty).
 *
 * @param {Array} rows Parsed ICD rows.
 * @param {{ uid?: string, email?: string }} [actor] Who is performing the upload.
 *   Stored on the meta document so the Diagnosis Codes summary view can show
 *   "Last uploaded: <date> by <email>".
 */
export async function replaceAllDiagnosisCodes(rows, actor = {}) {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error('No valid rows to import.');
  }
  const inputCount = rows.length;
  const deduped = dedupeRowsByIcdDocId(rows);
  if (deduped.length === 0) {
    throw new Error('No valid rows to import after deduplicating by ICD code.');
  }
  const dupMerged = inputCount - deduped.length;
  const enriched = enrichDiagnosisRowsWithAssignableAndCategory(deduped);

  const prepared = [];
  let truncatedLongDescCount = 0;
  enriched.forEach((r) => {
    const { row, truncated } = fitDiagnosisRowToFirestoreLimit(r);
    if (truncated) {
      truncatedLongDescCount += 1;
      const origLen = typeof r.longDesc === 'string' ? r.longDesc.length : 0;
      console.warn(`${LOG_PREFIX} Truncated longDesc (Firestore ~1MiB/doc limit)`, {
        orderNum: row.orderNum,
        icdCode: row.icdCode,
        originalChars: origLen,
        keptChars: row.longDesc?.length ?? 0,
      });
    }
    prepared.push(row);
  });

  console.info(`${LOG_PREFIX} Replace import: ${inputCount} input row(s) → ${deduped.length} after dedupe` +
    (dupMerged > 0 ? ` (${dupMerged} duplicate ICD line(s) merged)` : '') +
    ` → ${prepared.length} to write` +
    (truncatedLongDescCount > 0 ? ` (${truncatedLongDescCount} longDesc truncated for size)` : '') +
    '.');

  await deleteAllInChunks();
  await writeAllInChunks(prepared);
  await writeSubCategories(enriched);
  await buildAndUploadSearchBundle(enriched);

  /* Stamp the meta doc with summary fields so the (mobile-optimized)
     DiagnosisCodes page can render a summary view without reading the entire
     ~96k document collection. Fields are additive — existing search-bundle
     fields are preserved via merge. */
  try {
    await setDoc(
      doc(db, META_COLLECTION, META_DOC_ID),
      {
        count: prepared.length,
        uploadedAt: serverTimestamp(),
        uploadedByUid: actor?.uid || null,
        uploadedByEmail: actor?.email || null,
      },
      { merge: true }
    );
  } catch (err) {
    console.warn(`${LOG_PREFIX} Failed to stamp meta doc with upload summary`, err);
  }
}
